import { apiSlice } from './apiSlice';
import { decodeToken, encodeToken } from '../../utils/TokenUtils';

export const watchApi = apiSlice.injectEndpoints({
	endpoints: builder => ({
		getAllWatches: builder.query({
			query: () => `/api/v1/watches`,
			transformResponse: (response: any) => {
				const data = response?.data || response?.message || response;
				if (data && data.token) return decodeToken(data.token);
				return data;
			},
			providesTags: ['Watch'],
		}),
		getWatchById: builder.query({
			query: (watchId) => `/api/v1/watch/id/${watchId}`,
			transformResponse: (response: any) => {
				const data = response?.data || response?.message || response;
				if (data && data.token) return decodeToken(data.token);
				return data;
			},
			providesTags: (result, error, id) => [{ type: 'Watch' as const, id }, 'Watch'],
		}),
		getWatchByImei: builder.query({
			query: (imei) => `/api/v1/watch/${imei}`,
			transformResponse: (response: any) => {
				const data = response?.data || response?.message || response;
				if (data && data.token) return decodeToken(data.token);
				return data;
			},
			providesTags: (result, error, imei) => [{ type: 'Watch' as const, id: imei }, 'Watch'],
		}),
		getAllWatchesToCenter: builder.query({
			query: (centerId) => `/api/v1/watches/${centerId}`,
			transformResponse: (response: any) => {
				const data = response?.data || response?.message || response;
				if (data && data.token) return decodeToken(data.token);
				return data;
			},
			providesTags: ['Watch'],
		}),
		createWatch: builder.mutation({
			query: (data) => {
				const token = encodeToken(data);
				return {
					url: `/api/v1/watches`,
					method: 'POST',
					body: { token },
				};
			},
			invalidatesTags: ['Watch'],
		}),
		updateWatch: builder.mutation({
			query: (data) => {
				const { watchId, ...rest } = data;
				const token = encodeToken(data); // The old code encoded the WHOLE data including watchId.
				return {
					url: `/api/v1/watches/${watchId}`,
					method: 'PATCH',
					body: { token },
				};
			},
			invalidatesTags: (result, error, arg) => [{ type: 'Watch' as const, id: arg.watchId }, 'Watch'],
		}),
		deleteWatch: builder.mutation({
			query: (watchId) => ({
				url: `/api/v1/watches/${watchId}`,
				method: 'DELETE',
			}),
			invalidatesTags: ['Watch'],
		}),
		findWatch: builder.mutation({
			query: (imei) => ({
				url: `/api/v1/watch/socket/find/${encodeURIComponent(String(imei).replace(/\D/g, ''))}`,
				method: 'POST',
			}),
		}),
		refreshWatchVitalsAndLocation: builder.mutation({
			query: ({ imei, ...body }: { imei: string | number; [key: string]: any }) => {
				const imeiNorm = encodeURIComponent(String(imei).replace(/\D/g, ''));
				return {
					url: `/api/v1/watch/socket/update/vitalsAndLocationData/${imeiNorm}`,
					method: 'POST',
					body: { imei: String(imei).replace(/\D/g, ''), force: true, ...body },
				};
			},
			invalidatesTags: (result, error, { imei }) => [{ type: 'WatchData' as const, id: imei }],
		}),
		assignWatch: builder.mutation<any, { watchId: string | number; centerId?: any; patientId?: any }>({
			query: ({ watchId, centerId, patientId }) => ({
				url: `/api/v1/watch/assing/patientCenter/${watchId}`,
				method: 'PATCH',
				body: { centerId, patientId },
			}),
			invalidatesTags: (result, error, { watchId }) => [
				{ type: 'Watch' as const, id: watchId },
				'Watch',
				'Patient',
			],
		}),
		assignWatchCorporation: builder.mutation<any, { watchId: string | number; corporationId?: string | null }>({
			query: ({ watchId, corporationId }) => ({
				url: `/api/v1/watch/assign/corporation/${watchId}`,
				method: 'PATCH',
				body: { corporationId },
			}),
			invalidatesTags: (result, error, { watchId }) => [
				{ type: 'Watch' as const, id: watchId },
				'Watch',
			],
		}),
	}),
});

export const {
	useGetAllWatchesQuery,
	useGetWatchByIdQuery,
	useGetWatchByImeiQuery,
	useGetAllWatchesToCenterQuery,
	useCreateWatchMutation,
	useUpdateWatchMutation,
	useDeleteWatchMutation,
	useFindWatchMutation,
	useRefreshWatchVitalsAndLocationMutation,
	useAssignWatchMutation,
	useAssignWatchCorporationMutation,
} = watchApi;
